import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !password.trim()) {
      setError("Please fill in all fields");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await register(name, email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mesh-background font-body text-slate-800 antialiased min-h-screen relative overflow-hidden" data-testid="register-page">
      <style dangerouslySetInnerHTML={{__html: `
        .mesh-background {
            background-color: #f7f9fb;
            background-image: 
                radial-gradient(at 0% 0%, rgba(77, 68, 227, 0.15) 0px, transparent 50%),
                radial-gradient(at 100% 0%, rgba(218, 226, 253, 0.3) 0px, transparent 50%),
                radial-gradient(at 100% 100%, rgba(77, 68, 227, 0.1) 0px, transparent 50%),
                radial-gradient(at 0% 100%, rgba(190, 198, 224, 0.2) 0px, transparent 50%);
        }
        .glass-card {
            background: rgba(255, 255, 255, 0.7);
            backdrop-filter: blur(16px);
            -webkit-backdrop-filter: blur(16px);
            border: 1px solid rgba(255, 255, 255, 0.4);
        }
        .hero-image-mask {
            mask-image: linear-gradient(to right, black 60%, transparent 100%);
            -webkit-mask-image: linear-gradient(to right, black 60%, transparent 100%);
        }
        .floating-anim {
            animation: floating 6s ease-in-out infinite;
        }
        @keyframes floating {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
            100% { transform: translateY(0px); }
        }
      `}} />
      <main className="min-h-screen flex items-center justify-center lg:justify-start px-6 lg:px-20 relative z-10">
        {/* Hero Layout Wrapper */}
        <div className="flex flex-col lg:flex-row items-center w-full max-w-7xl mx-auto gap-12 lg:gap-24 relative z-10">
          
          {/* Left Section: Register Card */}
          <div className="w-full max-w-md shrink-0">
            {/* Logo Section */}
            <div className="flex flex-col items-center lg:items-start mb-10">
              <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center mb-5 shadow-xl shadow-indigo-600/30">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h1 className="text-3xl font-extrabold tracking-widest uppercase text-slate-900" style={{ fontFamily: 'Outfit, Plus Jakarta Sans, sans-serif' }}>UPA TIK</h1>
              <p className="text-slate-500 font-medium text-sm mt-1 tracking-wider uppercase opacity-70">Project Tracker</p>
            </div>
            
            {/* Form Card with Glassmorphism */}
            <div className="glass-card rounded-3xl shadow-[0_32px_64px_-16px_rgba(0,0,0,0.08)] p-8 md:p-12">
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, Plus Jakarta Sans, sans-serif' }}>Create an account</h2>
                <p className="text-slate-500 text-sm mt-2 font-medium">Join us to manage your workspace</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm text-center font-medium animate-in fade-in slide-in-from-top-2">
                    {error}
                  </div>
                )}
                {/* Name Field */}
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500" htmlFor="name">Full Name</label>
                  <div className="relative group">
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    <input 
                      className="w-full pl-12 pr-4 py-4 bg-white/50 border border-white/40 rounded-xl text-slate-800 placeholder:text-slate-400 focus:ring-4 focus:ring-indigo-600/10 focus:border-indigo-600/30 focus:bg-white transition-all duration-300 outline-none" 
                      id="name" 
                      name="name" 
                      placeholder="John Doe" 
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                {/* Email Field */}
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500" htmlFor="email">Email Address</label>
                  <div className="relative group">
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    <input 
                      className="w-full pl-12 pr-4 py-4 bg-white/50 border border-white/40 rounded-xl text-slate-800 placeholder:text-slate-400 focus:ring-4 focus:ring-indigo-600/10 focus:border-indigo-600/30 focus:bg-white transition-all duration-300 outline-none" 
                      id="email" 
                      name="email" 
                      placeholder="@staff.unri.ac.id" 
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>
                {/* Password Field */}
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500" htmlFor="password">Password</label>
                  <div className="relative group">
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                    <input 
                      className="w-full pl-12 pr-4 py-4 bg-white/50 border border-white/40 rounded-xl text-slate-800 placeholder:text-slate-400 focus:ring-4 focus:ring-indigo-600/10 focus:border-indigo-600/30 focus:bg-white transition-all duration-300 outline-none" 
                      id="password" 
                      name="password" 
                      placeholder="••••••••" 
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                </div>
                {/* Sign Up Button */}
                <button 
                  className="w-full py-4 bg-indigo-600 text-white font-bold rounded-xl shadow-xl shadow-indigo-600/25 hover:shadow-indigo-600/40 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-2 group" 
                  type="submit"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign Up"}
                  {!loading && <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>}
                </button>
                
                <div className="text-center mt-6">
                  <p className="text-sm text-slate-500 font-medium">
                    Already have an account? <Link to="/login" className="text-indigo-600 hover:text-indigo-800 font-bold transition-colors">Sign In</Link>
                  </p>
                </div>
              </form>
            </div>
          </div>

          {/* Right Section: Visual Content (Desktop Only) */}
          <div className="hidden lg:flex flex-1 items-center justify-center relative min-h-[600px]">
            {/* Architectural Graphics */}
            <div className="relative w-full max-w-xl aspect-square">
              {/* Main Illustration Layer */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-200 to-blue-200/50 rounded-[4rem] rotate-3 opacity-50"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <img 
                  className="w-full h-full object-cover rounded-[3rem] shadow-2xl -rotate-3 hero-image-mask" 
                  alt="Abstract modern architecture lines and structures" 
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuD3PRCMa5-OfP4sAqJRCFVfwg3rp3Imm1R66dB4PZXSkVx-dZADN_laRUoR-UnhL9aQHjprOaBJkllya_sS7hWB0HhnLb1SynnObJhvFd9_FqSdZrkgvQAca3tdLHK_MnANUVLAVqa5ryFmLO8nHMETxiFfQX_NY254h2GyPyX2Wf2GiK4MP1XocZ9gGSihD5Lyl0edwS7aYg2sGWeQoMsAFCYkfigeskHCKGWy7HM8JrBXVViCIWTbmJKwDwdMPuDMaDqNOSyehko"
                />
              </div>
              
              {/* Floating 3D-like UI Elements */}
              <div className="absolute -top-10 -right-10 w-48 p-6 glass-card rounded-2xl shadow-xl floating-anim" style={{ animationDelay: '-1s' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-white">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <div className="flex-1 h-2 bg-slate-200 rounded-full"></div>
                </div>
                <div className="space-y-2">
                  <div className="w-full h-1.5 bg-slate-200 rounded-full"></div>
                  <div className="w-2/3 h-1.5 bg-slate-200 rounded-full"></div>
                </div>
              </div>
              
              <div className="absolute bottom-10 -left-12 w-56 p-6 glass-card rounded-2xl shadow-2xl floating-anim" style={{ animationDelay: '-3s' }}>
                <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 mb-3">Project Progress</p>
                <div className="flex items-end gap-1 h-12">
                  <div className="w-full h-1/2 bg-indigo-200 rounded-t-sm"></div>
                  <div className="w-full h-2/3 bg-indigo-400 rounded-t-sm"></div>
                  <div className="w-full h-full bg-indigo-600 rounded-t-sm"></div>
                  <div className="w-full h-3/4 bg-indigo-500 rounded-t-sm"></div>
                  <div className="w-full h-1/2 bg-indigo-300 rounded-t-sm"></div>
                </div>
              </div>
              
              {/* Decorative Blur Orbs */}
              <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-indigo-600/5 blur-[120px] rounded-full"></div>
            </div>
          </div>
        </div>

        {/* Global Background Elements */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-600/5 blur-[150px] rounded-full -mr-40 -mt-40 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/10 blur-[150px] rounded-full -ml-40 -mb-40 pointer-events-none"></div>
      </main>
    </div>
  );
}